import { sortBy } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    deepStringFind,
    errorRedirect,
    inject,
    ModalHandle,
    NothingHere,
    SubTopBar,
    Title,
    toArray,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsArrowLeftShort } from '@react-icons/all-files/bs/BsArrowLeftShort';
import { BsCardHeading } from '@react-icons/all-files/bs/BsCardHeading';
import { RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { Button, Input } from 'reactstrap';
import { CreateOrEditModelDefinition } from '../ModelDefinitons/CreateOrEditModelDefinition';
import { ModelDefinitionsStore } from '../ModelDefinitons/ModelDefinitionsStore';
import { ProductionProjectsStore } from './ProductionProjectsStore';

export interface IEditProjectModelsParams {
    guid: string;
    name: string;
}

export interface IEditProjectModelsProps extends RouteComponentProps<IEditProjectModelsParams> {}

@autoinject
@observer
export class EditProjectModels extends React.Component<IEditProjectModelsProps> {
    @action
    public async componentDidMount() {
        this.loading = true;
        try {
            await this.modelDefinitionsStore.loadAllAsync();
            await this.productionProjectsStore.loadModelsForProject(this.props.match.params.guid);
        } catch (e: any) {
            errorRedirect(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        const location = `/production-projects/projects`;

        return (
            <div className="container-fluid">
                <Title>Edit Models For {this.props.match.params.name}</Title>

                <SubTopBar>
                    <LinkContainer exact to={location}>
                        <a className="btn btn-link">
                            <BsArrowLeftShort style={{ fontSize: 25 }} /> Back To Projects
                        </a>
                    </LinkContainer>

                    <Input
                        type="text"
                        value={this.searchValue}
                        onChange={this.onSearchInputChange}
                        className="mw-450"
                        placeholder="Search Models..."
                    />

                    <div className="mx-2 border" />

                    <button className="btn btn-primary" onClick={this.modalHandle.open}>
                        Create Model Definition
                    </button>
                </SubTopBar>

                <div className="row">
                    <div className="col-5 px-0">
                        <table className="table table-striped w-100">
                            <thead>
                                <tr>
                                    <th>Model Number</th>
                                    <th>Remove</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.projectModels.map(m => (
                                    <tr key={m.modelDefinitionGuid}>
                                        <td className="align-middle">{m.modelNumber}</td>

                                        <td>
                                            <Button color="primary" onClick={e => this.remove(e, m)}>
                                                Remove
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <NothingHere
                            empty={this.projectModels.length <= 0}
                            icon={<BsCardHeading />}
                            loading={this.loading}
                        />
                    </div>

                    <div className="col-7 px-0">
                        <table className="table table-striped w-100">
                            <thead>
                                <tr>
                                    <th>Model Number</th>
                                    <th>Part Number</th>
                                    <th>Description</th>
                                    <th>Add</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.modelDefinitions.map(m => (
                                    <tr key={m.modelDefinitionGuid}>
                                        <td className="align-middle">{m.modelNumber}</td>
                                        <td className="align-middle">{m.partNumber}</td>
                                        <td className="align-middle">{m.description}</td>
                                        <td>
                                            <Button color="primary" onClick={e => this.add(e, m)}>
                                                Add
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <NothingHere
                            empty={this.modelDefinitions.length <= 0}
                            icon={<BsCardHeading />}
                            loading={this.loading}
                        />
                    </div>

                    <CreateOrEditModelDefinition modalHandle={this.modalHandle} viewState="create" />
                </div>
            </div>
        );
    }

    @bind
    @action
    private async add(
        event: React.MouseEvent<HTMLButtonElement>,
        modelDefinition: Mri.ModelDefinitions.ModelDefinition
    ) {
        event.preventDefault();
        await this.productionProjectsStore.addModelAsync(this.props.match.params.guid, modelDefinition);
    }

    @bind
    @action
    private async remove(
        event: React.MouseEvent<HTMLButtonElement>,
        modelDefinition: Mri.ModelDefinitions.ModelDefinition
    ) {
        event.preventDefault();
        await this.productionProjectsStore.removeModelAsync(
            this.props.match.params.guid,
            modelDefinition.modelDefinitionGuid
        );
    }

    @bind
    @action
    private async onSearchInputChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.searchValue = target.value;
    }

    @computed
    private get modelDefinitions() {
        let models = toArray(this.modelDefinitionsStore.modelDefinitions);

        if (this.searchValue) {
            models = models!.filter(c => {
                const s = {
                    config: c,
                };

                return deepStringFind(s, this.searchValue);
            });
        }

        models = models.filter(c => {
            return !this.projectModels.find(e => e.modelDefinitionGuid == c.modelDefinitionGuid);
        });

        models = sortBy(models, a => a.modelNumber);
        return models;
    }

    @computed
    private get projectModels() {
        const models = this.productionProjectsStore.models.has(this.props.match.params.guid)
            ? toArray(this.productionProjectsStore.models.get(this.props.match.params.guid)!)
            : [];
        return models;
    }

    private modalHandle = new ModalHandle();
    @inject
    private readonly modelDefinitionsStore!: ModelDefinitionsStore;
    @inject
    private readonly productionProjectsStore!: ProductionProjectsStore;
    @observable private searchValue: string = '';
    @observable private loading = false;
}
